import { z } from 'zod';
const opt = (value, labelKey, hintKey, ai) => ({ value, labelKey, hintKey, ai });
// ── kind ──────────────────────────────────────────────────────────────────────
// A section is a full-width band of a page. A block is a repeatable unit that
// lives inside a section (one card in a card grid, one tier in a pricing table).
export const TEMPLATE_KINDS = [
    opt('section', 'tax.kind.section', 'tax.kind.section.hint', 'Full-width page section. Insert as a whole.'),
    opt('block', 'tax.kind.block', 'tax.kind.block.hint', 'Repeatable unit inside a section (a card, a pricing tier, a FAQ row). Insert into an existing section.'),
];
// ── category — WHAT the section is ─────────────────────────────────────────────
export const TEMPLATE_CATEGORIES = [
    opt('navbar', 'tax.category.navbar', 'tax.category.navbar.hint', 'Top navigation / header bar.'),
    opt('hero', 'tax.category.hero', 'tax.category.hero.hint', 'Above-the-fold headline section with primary call to action.'),
    opt('features', 'tax.category.features', 'tax.category.features.hint', 'Grid or list explaining product features / benefits.'),
    opt('cards', 'tax.category.cards', 'tax.category.cards.hint', 'Generic card grid of items.'),
    opt('bento', 'tax.category.bento', 'tax.category.bento.hint', 'Asymmetric bento-box grid of mixed-size tiles.'),
    opt('gallery', 'tax.category.gallery', 'tax.category.gallery.hint', 'Image gallery / portfolio / media mosaic.'),
    opt('logos', 'tax.category.logos', 'tax.category.logos.hint', 'Row of client / partner / brand logos ("as seen on").'),
    opt('stats', 'tax.category.stats', 'tax.category.stats.hint', 'Row of key numbers / metrics / KPIs.'),
    opt('testimonials', 'tax.category.testimonials', 'tax.category.testimonials.hint', 'Customer quotes, reviews, social proof.'),
    opt('pricing', 'tax.category.pricing', 'tax.category.pricing.hint', 'Pricing plans / tiers / comparison.'),
    opt('faq', 'tax.category.faq', 'tax.category.faq.hint', 'Frequently asked questions, usually an accordion.'),
    opt('cta', 'tax.category.cta', 'tax.category.cta.hint', 'Focused conversion band with a single action.'),
    opt('contact', 'tax.category.contact', 'tax.category.contact.hint', 'Contact / lead-capture form section.'),
    opt('menu', 'tax.category.menu', 'tax.category.menu.hint', 'Restaurant, café or bar menu: one course with its dishes, each with a short description and its price, and an order-online button. Only for food and drink businesses.'),
    opt('reservations', 'tax.category.reservations', 'tax.category.reservations.hint', 'Restaurant table reservations with the OpenTable widget. Only for restaurants, bars and cafés that take table bookings on OpenTable.'),
    opt('map', 'tax.category.map', 'tax.category.map.hint', 'Where to find the business: a map with the address, hours and directions, or several branches/locations each with its own map.'),
    opt('team', 'tax.category.team', 'tax.category.team.hint', 'Team members / people profiles.'),
    opt('steps', 'tax.category.steps', 'tax.category.steps.hint', 'Step-by-step process / timeline / "how it works".'),
    opt('content', 'tax.category.content', 'tax.category.content.hint', 'Long-form rich text / article body.'),
    opt('about', 'tax.category.about', 'tax.category.about.hint', 'About / our story: several paragraphs telling the story of the person or the business (how it started, what drives it), with photos.'),
    opt('comparison', 'tax.category.comparison', 'tax.category.comparison.hint', 'Side-by-side comparison table.'),
    opt('banner', 'tax.category.banner', 'tax.category.banner.hint', 'Announcement / promo bar.'),
    opt('blog', 'tax.category.blog', 'tax.category.blog.hint', 'List of blog posts / news cards.'),
    opt('commerce', 'tax.category.commerce', 'tax.category.commerce.hint', 'Product cards / e-commerce listing.'),
    opt('footer', 'tax.category.footer', 'tax.category.footer.hint', 'Page footer with links and legal.'),
];
// ── layout — HOW it is arranged ────────────────────────────────────────────────
export const TEMPLATE_LAYOUTS = [
    opt('stack', 'tax.layout.stack', 'tax.layout.stack.hint', 'Single centered column, stacked vertically.'),
    opt('grid', 'tax.layout.grid', 'tax.layout.grid.hint', 'Uniform grid of equal cells.'),
    opt('columns', 'tax.layout.columns', 'tax.layout.columns.hint', 'Multiple side-by-side columns.'),
    opt('split', 'tax.layout.split', 'tax.layout.split.hint', 'Two-pane split, text on one side and media on the other.'),
    opt('carousel', 'tax.layout.carousel', 'tax.layout.carousel.hint', 'Horizontal slider / carousel.'),
    opt('masonry', 'tax.layout.masonry', 'tax.layout.masonry.hint', 'Masonry layout with uneven heights.'),
    opt('list', 'tax.layout.list', 'tax.layout.list.hint', 'Stacked list of rows.'),
    opt('table', 'tax.layout.table', 'tax.layout.table.hint', 'Tabular rows and columns.'),
    opt('overlay', 'tax.layout.overlay', 'tax.layout.overlay.hint', 'Text overlaid on a background image or video.'),
];
// ── columns — density of a grid/columns layout ─────────────────────────────────
export const TEMPLATE_COLUMNS = [
    opt('1', 'tax.columns.1', 'tax.columns.1.hint', 'One column.'),
    opt('2', 'tax.columns.2', 'tax.columns.2.hint', 'Two columns.'),
    opt('3', 'tax.columns.3', 'tax.columns.3.hint', 'Three columns.'),
    opt('4', 'tax.columns.4', 'tax.columns.4.hint', 'Four columns.'),
    opt('5', 'tax.columns.5', 'tax.columns.5.hint', 'Five columns.'),
    opt('6', 'tax.columns.6', 'tax.columns.6.hint', 'Six columns.'),
    opt('auto', 'tax.columns.auto', 'tax.columns.auto.hint', 'Column count adapts to content.'),
];
// ── style — visual aesthetic ───────────────────────────────────────────────────
// Sólo para templates de sitio. Un bloque es estructura, como en Relume: se ve
// como diga la receta de estilo del sitio, así que no lleva `style`, `goal` ni
// `bestFor` — ni la ficha los pide ni el ranking los puntúa.
export const TEMPLATE_STYLES = [
    opt('minimal', 'tax.style.minimal', 'tax.style.minimal.hint', 'Clean, restrained, generous whitespace.'),
    opt('modern', 'tax.style.modern', 'tax.style.modern.hint', 'Contemporary, balanced, mainstream SaaS look.'),
    opt('bold', 'tax.style.bold', 'tax.style.bold.hint', 'High contrast, oversized type, strong statements.'),
    opt('elegant', 'tax.style.elegant', 'tax.style.elegant.hint', 'Refined, understated, premium feel.'),
    opt('playful', 'tax.style.playful', 'tax.style.playful.hint', 'Vivid colors, rounded shapes, friendly.'),
    opt('corporate', 'tax.style.corporate', 'tax.style.corporate.hint', 'Serious, trustworthy, business-oriented.'),
    opt('editorial', 'tax.style.editorial', 'tax.style.editorial.hint', 'Typography-led, magazine style.'),
    opt('glass', 'tax.style.glass', 'tax.style.glass.hint', 'Glassmorphism, translucent frosted surfaces.'),
];
// ── colorScheme — background intent ────────────────────────────────────────────
export const TEMPLATE_COLOR_SCHEMES = [
    opt('light', 'tax.colorScheme.light', 'tax.colorScheme.light.hint', 'Light background.'),
    opt('dark', 'tax.colorScheme.dark', 'tax.colorScheme.dark.hint', 'Dark background.'),
    opt('muted', 'tax.colorScheme.muted', 'tax.colorScheme.muted.hint', 'Soft muted / gray background.'),
    opt('gradient', 'tax.colorScheme.gradient', 'tax.colorScheme.gradient.hint', 'Gradient background.'),
    opt('image', 'tax.colorScheme.image', 'tax.colorScheme.image.hint', 'Image or video background.'),
];
// ── media — dominant visual element ────────────────────────────────────────────
export const TEMPLATE_MEDIA = [
    opt('none', 'tax.media.none', 'tax.media.none.hint', 'Text only, no imagery.'),
    opt('icon', 'tax.media.icon', 'tax.media.icon.hint', 'Uses icons.'),
    opt('image', 'tax.media.image', 'tax.media.image.hint', 'Uses images or illustrations.'),
    opt('video', 'tax.media.video', 'tax.media.video.hint', 'Uses video.'),
    opt('avatar', 'tax.media.avatar', 'tax.media.avatar.hint', 'Uses people avatars / headshots.'),
    opt('logo', 'tax.media.logo', 'tax.media.logo.hint', 'Uses brand logos.'),
];
// ── goal — conversion / editorial intent ───────────────────────────────────────
export const TEMPLATE_GOALS = [
    opt('conversion', 'tax.goal.conversion', 'tax.goal.conversion.hint', 'Drive the visitor toward a primary action.'),
    opt('trust', 'tax.goal.trust', 'tax.goal.trust.hint', 'Build trust with social proof / credibility.'),
    opt('education', 'tax.goal.education', 'tax.goal.education.hint', 'Explain how something works.'),
    opt('capture', 'tax.goal.capture', 'tax.goal.capture.hint', 'Capture a lead / contact details.'),
    opt('showcase', 'tax.goal.showcase', 'tax.goal.showcase.hint', 'Showcase work, portfolio or products.'),
];
// ── blockType — WHAT a repeatable block is ─────────────────────────────────────
// Only meaningful when kind === 'block'. Lets the AI drop the right repeatable unit
// into a matching section ("add another pricing tier").
export const TEMPLATE_BLOCK_TYPES = [
    opt('feature-card', 'tax.blockType.feature-card', 'tax.blockType.feature-card.hint', 'Icon + title + body card.'),
    opt('pricing-tier', 'tax.blockType.pricing-tier', 'tax.blockType.pricing-tier.hint', 'One pricing plan column.'),
    opt('testimonial', 'tax.blockType.testimonial', 'tax.blockType.testimonial.hint', 'Quote with author.'),
    opt('faq-item', 'tax.blockType.faq-item', 'tax.blockType.faq-item.hint', 'One question/answer row.'),
    opt('stat', 'tax.blockType.stat', 'tax.blockType.stat.hint', 'One number + label.'),
    opt('team-member', 'tax.blockType.team-member', 'tax.blockType.team-member.hint', 'One person with role.'),
    opt('step', 'tax.blockType.step', 'tax.blockType.step.hint', 'One numbered process step.'),
    opt('gallery-item', 'tax.blockType.gallery-item', 'tax.blockType.gallery-item.hint', 'One gallery image.'),
    opt('logo', 'tax.blockType.logo', 'tax.blockType.logo.hint', 'One brand logo.'),
    opt('nav-link', 'tax.blockType.nav-link', 'tax.blockType.nav-link.hint', 'One navigation link.'),
    opt('form-field', 'tax.blockType.form-field', 'tax.blockType.form-field.hint', 'One form field.'),
    opt('accordion-item', 'tax.blockType.accordion-item', 'tax.blockType.accordion-item.hint', 'One collapsible accordion row.'),
];
// ── bestFor — WHO it suits (industry) ──────────────────────────────────────────
// The single strongest signal when choosing for a real business: a hero built
// for a restaurant (food photo, reservation CTA) is not the one for a SaaS.
// `general` marks templates whose copy and imagery fit anything.
export const TEMPLATE_INDUSTRIES = [
    opt('general', 'tax.bestFor.general', 'tax.bestFor.general.hint', 'Neutral copy and imagery; fits any business.'),
    opt('saas', 'tax.bestFor.saas', 'tax.bestFor.saas.hint', 'Software, SaaS, mobile or web apps, digital products.'),
    opt('agency', 'tax.bestFor.agency', 'tax.bestFor.agency.hint', 'Creative, marketing, design or development agencies and studios.'),
    opt('consulting', 'tax.bestFor.consulting', 'tax.bestFor.consulting.hint', 'Consulting, legal, accounting, advisory and B2B professional services.'),
    opt('ecommerce', 'tax.bestFor.ecommerce', 'tax.bestFor.ecommerce.hint', 'Online stores, product catalogs, direct-to-consumer brands.'),
    opt('restaurant', 'tax.bestFor.restaurant', 'tax.bestFor.restaurant.hint', 'Restaurants, cafés, bars, bakeries, catering.'),
    opt('health', 'tax.bestFor.health', 'tax.bestFor.health.hint', 'Medical clinics, dentists, therapists, healthcare providers.'),
    opt('wellness', 'tax.bestFor.wellness', 'tax.bestFor.wellness.hint', 'Beauty salons, spas, barbers, wellness and self-care.'),
    opt('fitness', 'tax.bestFor.fitness', 'tax.bestFor.fitness.hint', 'Gyms, personal trainers, yoga and sports studios.'),
    opt('education', 'tax.bestFor.education', 'tax.bestFor.education.hint', 'Online courses, schools, tutoring, coaching programs.'),
    opt('realestate', 'tax.bestFor.realestate', 'tax.bestFor.realestate.hint', 'Real estate agents, property listings, rentals.'),
    opt('finance', 'tax.bestFor.finance', 'tax.bestFor.finance.hint', 'Fintech, banking, investing, insurance.'),
    opt('creator', 'tax.bestFor.creator', 'tax.bestFor.creator.hint', 'Creators, influencers, speakers, personal brands.'),
    opt('portfolio', 'tax.bestFor.portfolio', 'tax.bestFor.portfolio.hint', 'Photographers, designers, artists, creative portfolios.'),
    opt('events', 'tax.bestFor.events', 'tax.bestFor.events.hint', 'Events, conferences, venues, weddings.'),
    opt('hospitality', 'tax.bestFor.hospitality', 'tax.bestFor.hospitality.hint', 'Hotels, vacation rentals, tours and travel.'),
    opt('trades', 'tax.bestFor.trades', 'tax.bestFor.trades.hint', 'Construction, contractors, cleaning, repair and home services.'),
    opt('nonprofit', 'tax.bestFor.nonprofit', 'tax.bestFor.nonprofit.hint', 'Nonprofits, charities, churches, community groups.'),
];
// ── data — where a block's items come from ──────────────────────────────────────
// Un bloque con datos no trae elementos de ejemplo: los lee del proyecto donde
// se inserta —sus servicios, sus productos digitales, sus reservas—. No es un
// filtro de la biblioteca: lo usan la IA y los templates de página para elegir,
// cuando el negocio vende eso, el bloque que ya sale con lo suyo.
export const TEMPLATE_DATA_SOURCES = [
    opt('offers', 'tax.data.offers', 'tax.data.offers.hint', 'Lists all of the business\'s own offers.'),
    opt('offers.services', 'tax.data.offers.services', 'tax.data.offers.services.hint', 'Lists the business\'s own services.'),
    opt('offers.digital', 'tax.data.offers.digital', 'tax.data.offers.digital.hint', 'Lists the business\'s own digital products.'),
    opt('offers.bookings', 'tax.data.offers.bookings', 'tax.data.offers.bookings.hint', 'Lists the business\'s own bookable services.'),
];
// ── language — the language a template's copy is written in ─────────────────────
// No es un filtro de la biblioteca: lo decide el idioma del SITIO. Un template
// en español no se ofrece a un sitio en inglés ni al revés; uno sin idioma
// (sin texto, o aún sin marcar) vale para todos. Ver `templateFitsLanguage`.
export const TEMPLATE_LANGUAGES = ['es', 'en'];
/**
 * Si un template sirve para un sitio en `language`: el de sus textos, uno de
 * los que tiene traducidos (`languages`, ver `templateTexts.ts`), o ninguno
 * marcado.
 */
export function templateFitsLanguage(facets, language) {
    if (!language || !facets?.language)
        return true;
    return facets.language === language || Boolean(facets.languages?.includes(language));
}
/**
 * Los templates para un sitio en `language`, sin quedarse sin ninguno.
 *
 * Fuera los de otro idioma, pero sólo cuando su grupo (por defecto, su
 * categoría) tiene alguno que sirva: si el único hero de la biblioteca está en
 * inglés, un sitio en español lo sigue teniendo —mejor un texto que traducir
 * que una sección que falta—.
 */
export function preferTemplateLanguage(templates, language, groupOf) {
    if (!language)
        return [...templates];
    const covered = new Set(templates.filter((template) => templateFitsLanguage(template.facets, language)).map(groupOf));
    return templates.filter((template) => templateFitsLanguage(template.facets, language) || !covered.has(groupOf(template)));
}
/**
 * The facet registry that drives the filter UI. Order here is the order shown.
 * `category` and `style` lead because they are what users reach for first.
 */
export const TEMPLATE_FACETS = [
    { key: 'category', labelKey: 'tax.facet.category', multiple: false, options: TEMPLATE_CATEGORIES },
    { key: 'style', labelKey: 'tax.facet.style', multiple: true, options: TEMPLATE_STYLES },
    { key: 'layout', labelKey: 'tax.facet.layout', multiple: true, options: TEMPLATE_LAYOUTS },
    { key: 'columns', labelKey: 'tax.facet.columns', multiple: false, options: TEMPLATE_COLUMNS },
    { key: 'colorScheme', labelKey: 'tax.facet.colorScheme', multiple: true, options: TEMPLATE_COLOR_SCHEMES },
    { key: 'media', labelKey: 'tax.facet.media', multiple: true, options: TEMPLATE_MEDIA },
    { key: 'goal', labelKey: 'tax.facet.goal', multiple: true, options: TEMPLATE_GOALS },
    { key: 'bestFor', labelKey: 'tax.facet.bestFor', multiple: true, options: TEMPLATE_INDUSTRIES },
];
const valuesOf = (options) => options.map((option) => option.value);
export const TEMPLATE_TAXONOMY_VALUES = {
    category: valuesOf(TEMPLATE_CATEGORIES),
    style: valuesOf(TEMPLATE_STYLES),
    layout: valuesOf(TEMPLATE_LAYOUTS),
    columns: valuesOf(TEMPLATE_COLUMNS),
    colorScheme: valuesOf(TEMPLATE_COLOR_SCHEMES),
    media: valuesOf(TEMPLATE_MEDIA),
    goal: valuesOf(TEMPLATE_GOALS),
    bestFor: valuesOf(TEMPLATE_INDUSTRIES),
};
/**
 * La CLAVE del nombre de un valor dentro de una faceta.
 *
 * Devuelve el valor crudo si no lo conoce, igual que antes: una plantilla vieja
 * con una categoría que ya no existe sigue enseñando algo en vez de nada. Ese
 * caso de reserva es la razón de que quien llama tenga que comprobar si el
 * traductor le devolvió la clave — ver `taxonomyText` en el editor.
 */
export function taxonomyLabelKey(facet, value) {
    return TEMPLATE_FACETS.find((entry) => entry.key === facet)?.options.find((option) => option.value === value)?.labelKey ?? value;
}
// ── template metadata: the tags a section/block carries ────────────────────────
/**
 * Structured facets attached to one template. Every field is optional so a
 * partially-tagged template still lists and filters; the AI simply has less to go
 * on. `category` and `kind` are the only ones worth treating as near-required.
 */
export const templateFacetsSchema = z.object({
    kind: z.enum(['section', 'block']).optional(),
    category: z.enum(TEMPLATE_TAXONOMY_VALUES.category).optional(),
    style: z.array(z.enum(TEMPLATE_TAXONOMY_VALUES.style)).max(TEMPLATE_STYLES.length).optional(),
    layout: z.array(z.enum(TEMPLATE_TAXONOMY_VALUES.layout)).max(TEMPLATE_LAYOUTS.length).optional(),
    columns: z.enum(TEMPLATE_TAXONOMY_VALUES.columns).optional(),
    colorScheme: z.array(z.enum(TEMPLATE_TAXONOMY_VALUES.colorScheme)).max(TEMPLATE_COLOR_SCHEMES.length).optional(),
    media: z.array(z.enum(TEMPLATE_TAXONOMY_VALUES.media)).max(TEMPLATE_MEDIA.length).optional(),
    goal: z.array(z.enum(TEMPLATE_TAXONOMY_VALUES.goal)).max(TEMPLATE_GOALS.length).optional(),
    bestFor: z.array(z.enum(TEMPLATE_TAXONOMY_VALUES.bestFor)).max(TEMPLATE_INDUSTRIES.length).optional(),
    /** De dónde salen sus elementos, si no son de ejemplo. Ver `TEMPLATE_DATA_SOURCES`. */
    data: z.enum(TEMPLATE_DATA_SOURCES.map((option) => option.value)).optional(),
    /** Only for kind === 'block'. */
    blockType: z.enum(TEMPLATE_BLOCK_TYPES.map((option) => option.value)).optional(),
    /** Repeatable-block bounds, used by the AI to know if it may add/remove instances. */
    blockMin: z.number().int().min(0).max(50).optional(),
    blockMax: z.number().int().min(1).max(50).optional(),
    /** El idioma de sus textos. Ausente = vale para cualquier sitio. Ver `TEMPLATE_LANGUAGES`. */
    language: z.enum(TEMPLATE_LANGUAGES).optional(),
    /**
     * Los idiomas en que se puede insertar: el suyo y los que tiene traducidos en
     * `templates_data.texts` (ver `templateTexts.ts`). Lo mantienen el script de
     * unificar y el admin; el filtro de la biblioteca lo lee de aquí.
     */
    languages: z.array(z.enum(TEMPLATE_LANGUAGES)).max(TEMPLATE_LANGUAGES.length).optional(),
    /**
     * Una copia vieja por idioma, ya escondida: el id del template que la
     * sustituye. Un proyecto o una receta que guarde su id lleva a ése.
     */
    mergedInto: z.string().uuid().optional(),
    /**
     * Sólo a mano: la IA no lo usa nunca (un bloque que sin el texto del cliente
     * sale vacío, como «Statement Panel»). Se sigue viendo en la biblioteca.
     */
    manualOnly: z.boolean().optional(),
    /**
     * Un bloque de VARIAS sucursales (2026-10-30): cuántas enseña —2, 3— o
     * `list` si es una lista que crece. Sin la faceta, una sola dirección. La IA
     * sólo los usa cuando el negocio tiene ese número de sucursales.
     */
    locations: z.union([z.literal(2), z.literal(3), z.literal('list')]).optional(),
}).strict();
const MULTI_FACET_KEYS = ['style', 'layout', 'colorScheme', 'media', 'goal', 'bestFor'];
const SINGLE_FACET_KEYS = ['category', 'columns'];
/**
 * Coerces the loose tag columns a template row actually stores (free-string
 * arrays authored over time) into typed, deduplicated facets. Unknown values are
 * dropped rather than trusted, so a stray tag never reaches the AI or the filter.
 */
export function normalizeTemplateFacets(raw) {
    const source = (raw && typeof raw === 'object' ? raw : {});
    const result = {};
    const asStrings = (value) => (Array.isArray(value) ? value : typeof value === 'string' ? [value] : [])
        .filter((entry) => typeof entry === 'string')
        .map((entry) => entry.trim().toLowerCase());
    if (source.kind === 'section' || source.kind === 'block')
        result.kind = source.kind;
    for (const key of SINGLE_FACET_KEYS) {
        const [first] = asStrings(source[key]);
        if (first && TEMPLATE_TAXONOMY_VALUES[key].includes(first))
            result[key] = first;
    }
    for (const key of MULTI_FACET_KEYS) {
        const allowed = new Set(TEMPLATE_TAXONOMY_VALUES[key]);
        const values = [...new Set(asStrings(source[key]).filter((entry) => allowed.has(entry)))];
        if (values.length > 0)
            result[key] = values;
    }
    const [data] = asStrings(source.data);
    if (data && TEMPLATE_DATA_SOURCES.some((option) => option.value === data))
        result.data = data;
    const [blockType] = asStrings(source.blockType);
    if (blockType && TEMPLATE_BLOCK_TYPES.some((option) => option.value === blockType))
        result.blockType = blockType;
    if (typeof source.blockMin === 'number' && Number.isInteger(source.blockMin))
        result.blockMin = source.blockMin;
    if (typeof source.blockMax === 'number' && Number.isInteger(source.blockMax))
        result.blockMax = source.blockMax;
    const [language] = asStrings(source.language);
    if (language && TEMPLATE_LANGUAGES.includes(language))
        result.language = language;
    const languages = [...new Set(asStrings(source.languages).filter((entry) => TEMPLATE_LANGUAGES.includes(entry)))];
    if (languages.length)
        result.languages = languages;
    if (typeof source.mergedInto === 'string' && source.mergedInto)
        result.mergedInto = source.mergedInto;
    if (source.manualOnly === true)
        result.manualOnly = true;
    if (source.locations === 2 || source.locations === 3 || source.locations === 'list')
        result.locations = source.locations;
    const parsed = templateFacetsSchema.safeParse(result);
    return parsed.success ? parsed.data : {};
}
/**
 * One compact catalog line the AI reads to choose a template by id. Kept terse on
 * purpose: the catalog can hold hundreds of these, so every token counts. Only
 * populated facets appear.
 *
 * Example:
 *   [hero-split-01] Hero dividido con imagen — hero · split · minimal · light · goal:conversion — "Titular, subtítulo y dos botones junto a una imagen."
 */
export function buildTemplateCatalogLine(template) {
    const { facets } = template;
    const parts = [];
    if (facets.category)
        parts.push(facets.category);
    if (facets.layout?.length)
        parts.push(facets.layout.join('/'));
    if (facets.columns)
        parts.push(facets.columns + 'col');
    if (facets.style?.length)
        parts.push(facets.style.join('/'));
    if (facets.colorScheme?.length)
        parts.push(facets.colorScheme.join('/'));
    if (facets.media?.length)
        parts.push('media:' + facets.media.join('/'));
    if (facets.goal?.length)
        parts.push('goal:' + facets.goal.join('/'));
    if (facets.bestFor?.length)
        parts.push('for:' + facets.bestFor.join('/'));
    if (facets.blockType)
        parts.push('block:' + facets.blockType);
    if (facets.data)
        parts.push('data:' + facets.data);
    const tags = parts.length > 0 ? ' — ' + parts.join(' · ') : '';
    const desc = template.description?.trim() ? ' — "' + template.description.trim().slice(0, 160) + '"' : '';
    return '[' + template.id + '] ' + template.name + tags + desc;
}
/**
 * The vocabulary block for the AI system prompt: what every facet value means, so
 * the model interprets a catalog line correctly instead of guessing. This is the
 * "best practices readable by the AI" layer — it makes the tags self-describing.
 * Static and cacheable; it does not change between requests.
 */
export function describeTemplateTaxonomy() {
    const lines = [
        'Vocabulario del catálogo de plantillas. Cada plantilla se describe con estas facetas ortogonales:',
    ];
    const render = (label, options) => {
        lines.push(label + ':');
        for (const option of options)
            lines.push('  ' + option.value + ' = ' + option.ai);
    };
    render('kind', TEMPLATE_KINDS);
    for (const facet of TEMPLATE_FACETS)
        render(facet.key, facet.options);
    render('blockType', TEMPLATE_BLOCK_TYPES);
    return lines.join('\n');
}
/**
 * Pre-filters the catalog before it is sent to the AI. Sending the model only the
 * templates that match the requested category/style keeps the prompt small AND
 * improves selection accuracy — the two goals pull the same direction. A facet
 * with no constraint is ignored; multi-value constraints match on overlap.
 */
export function filterCatalog(templates, constraints) {
    const wanted = (raw) => (Array.isArray(raw) ? raw : raw ? [raw] : []).map((entry) => entry.toLowerCase());
    // Facet values are only ever strings or string[]; anything else can't match.
    const asStringArray = (value) => {
        if (typeof value === 'string')
            return [value];
        if (Array.isArray(value))
            return value.filter((entry) => typeof entry === 'string');
        return [];
    };
    return templates.filter((template) => {
        for (const [key, raw] of Object.entries(constraints)) {
            const need = wanted(raw);
            if (need.length === 0)
                continue;
            const have = asStringArray(template.facets[key]);
            if (!need.some((entry) => have.includes(entry)))
                return false;
        }
        return true;
    });
}
//# sourceMappingURL=templateTaxonomy.js.map